// Mewdie: the optional "Priority Insured Shipping" add-on in Horizon's cart drawer and cart page (2026-10-12).
// Markup and config: snippets/mewdie-insured.liquid. The Mewdie product section keeps its own drawer and
// calls window.MewdieCart.notify(cart) after each change, so Horizon's drawer and cart bubble stay in step.
//
// Rules:
// - Tick = add the add-on once (POST /cart/add.js, quantity 1). Untick = remove it (POST /cart/change.js, quantity 0).
// - The add-on never goes above quantity 1.
// - When nothing but the add-on is left in the cart, it is removed.
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SOURCE = 'mewdie-insured';
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function readConfig() {
  const el = document.querySelector('script[data-mw-ins-config]');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent || 'null');
  } catch {
    return null;
  }
}

/** Every cart-items-component on the page, so one request returns the HTML each of them needs. */
function sectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return [...ids];
}

/**
 * @param {string} path
 * @param {object} body
 */
async function post(path, body) {
  const res = await fetch(root + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok || (json && json.status && json.status >= 400)) throw json;
  return json;
}

async function getCart() {
  const res = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('cart ' + res.status);
  return res.json();
}

/**
 * Tells the theme the cart changed (Horizon drawer, cart page, cart bubble).
 * Without section HTML, each cart-items-component fetches its own.
 * @param {any} cart - Full cart JSON (cart.js / change.js shape).
 * @param {Record<string, string>} [sections]
 * @param {EventTarget} [target]
 */
function notify(cart, sections, target) {
  if (!cart || !Array.isArray(cart.items)) return;
  const lines = cart.items.length
    ? cart.items.map((it) => ({ id: String(it.key), quantity: it.quantity }))
    : [{ id: 'empty', quantity: 0 }];
  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines, promise: deferred.promise });
  (target || document).dispatchEvent(event);
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { sections: sections || undefined, items: cart.items, itemCount: cart.item_count, source: SOURCE, didError: false },
  });
}

// Available even when the add-on product is missing, so the Mewdie drawer can keep Horizon in step.
window.MewdieCart = window.MewdieCart || { notify };

const config = readConfig();
const VARIANT = config && Number(config.variant);

if (VARIANT && !window.__mewdieInsured) {
  window.__mewdieInsured = true;

  /** @type {Promise<unknown>} One cart change at a time. */
  let queue = Promise.resolve();
  const enqueue = (/** @type {() => Promise<unknown>} */ fn) => (queue = queue.then(fn, fn));

  /**
   * Brings the add-on line to the wanted quantity (0 or 1). Returns the full cart after the change, or null.
   * @param {boolean} wanted - true = the buyer wants the add-on
   * @param {EventTarget} [target]
   */
  async function sync(wanted, target) {
    const cart = await getCart();
    const line = cart.items.find((it) => it.variant_id === VARIANT);
    const others = cart.items.some((it) => it.variant_id !== VARIANT);
    const want = wanted && others ? 1 : 0;
    const have = line ? line.quantity : 0;
    if (want === have) return cart;

    const ids = sectionIds();
    const extra = ids.length ? { sections: ids.join(','), sections_url: window.location.pathname } : {};
    let next;
    let sections;
    if (want === 1 && have === 0) {
      const added = await post('cart/add.js', { items: [{ id: VARIANT, quantity: 1 }], ...extra });
      sections = added && added.sections;
      next = await getCart();
    } else {
      next = await post('cart/change.js', { id: line.key, quantity: want, ...extra });
      sections = next.sections;
    }
    notify(next, sections, target);
    return next;
  }

  // Checkbox in the Horizon cart drawer / cart page.
  document.addEventListener('change', (event) => {
    const box = event.target;
    if (!(box instanceof HTMLInputElement) || !box.matches('[data-mw-ins-box]')) return;
    const wrap = box.closest('[data-mw-ins]');
    const err = wrap ? wrap.querySelector('[data-mw-ins-err]') : null;
    const wanted = box.checked;
    if (err) err.textContent = '';
    box.disabled = true;
    if (wrap) wrap.setAttribute('aria-busy', 'true');

    enqueue(() => sync(wanted, box))
      .catch(() => {
        box.checked = !wanted;
        if (err) err.textContent = 'Could not update your cart. Please try again.';
      })
      .finally(() => {
        // The section re-render may have replaced the input; settle every copy on the page.
        for (const el of document.querySelectorAll('[data-mw-ins-box]')) {
          if (el instanceof HTMLInputElement) el.disabled = false;
        }
        if (wrap) wrap.removeAttribute('aria-busy');
      });
  });

  // Any other cart change (remove a hoodie, change a quantity, add from a product form):
  // keep the add-on at 1 at most, and drop it when nothing else is left.
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const evt = /** @type {any} */ (event);
    if (!evt.promise) return;
    evt.promise
      .then((/** @type {any} */ result) => {
        if (result && result.detail && (result.detail.source === SOURCE || result.detail.didError)) return;
        return enqueue(async () => {
          const cart = await getCart();
          const line = cart.items.find((/** @type {any} */ it) => it.variant_id === VARIANT);
          if (!line) return;
          const others = cart.items.some((/** @type {any} */ it) => it.variant_id !== VARIANT);
          if (others && line.quantity === 1) return;
          await sync(others);
        });
      })
      .catch(() => {});
  });

  // The page was rendered from a cart that breaks the rules (e.g. /cart/add?id=…&quantity=2).
  if (config.fix) {
    enqueue(async () => {
      const cart = await getCart();
      const others = cart.items.some((/** @type {any} */ it) => it.variant_id !== VARIANT);
      await sync(others);
    }).catch(() => {});
  }
}
